/**
 * Customer Trips Page (Trip List View)
 *
 * Displays all customer trips with filtering and sorting:
 * - Upcoming trips
 * - Past trips
 * - Cancelled trips
 * - Filter by status, date range, guide
 * - Sort by date, price, guide name
 * - Search by experience name or guide
 *
 * Epic: MVP 1.21.4 - Customer Dashboard
 * Story: US-1.21.4-03: Trip List View (5 SP)
 *
 * Data Flow:
 * - Fetches from GET /api/v1/customer/trips
 * - Uses CustomerDashboardRepository.getUpcomingTrips()
 * - Uses CustomerDashboardRepository.getPastTrips()
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  Calendar,
  MapPin as _MapPin,
  DollarSign,
  Star as _Star,
  Filter,
  Search,
  ChevronDown as _ChevronDown,
  ChevronUp as _ChevronUp,
  Users as _Users,
  Clock as _Clock,
  AlertCircle
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import EmptyState from '../../components/common/EmptyState';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import { logError } from '../../services/errorLoggingService';
import { formatCentsToDollars } from '../../utils/formHelpers';

export default function CustomerTrips() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all'); // all, upcoming, past, cancelled
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // date, price, guide
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch trips
  useEffect(() => {
    const fetchTrips = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (statusFilter !== 'all') {params.append('status', statusFilter);}

        const response = await apiRequest(`/api/v1/customer/trips?${params.toString()}`, { method: 'GET' });
        if (response.success) {
          setTrips(response.data);
        }
      } catch (err) {
        logError(err, { componentName: 'CustomerTrips', action: 'fetch_trips' });
        if (err.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError(err.message || 'Failed to load trips');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTrips();
  }, [statusFilter]);


  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter and sort trips
  const filteredTrips = trips
    .filter((trip) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          trip.experienceName?.toLowerCase().includes(query) ||
          trip.guideName?.toLowerCase().includes(query) ||
          trip.location?.name?.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort((a, b) => {
      let compareA, compareB;

      switch (sortBy) {
        case 'date':
          compareA = new Date(a.tripDate);
          compareB = new Date(b.tripDate);
          break;
        case 'price':
          compareA = a.totalPaidCents;
          compareB = b.totalPaidCents;
          break;
        case 'guide':
          compareA = a.guideName;
          compareB = b.guideName;
          break;
        default:
          compareA = new Date(a.tripDate);
          compareB = new Date(b.tripDate);
      }

      if (sortOrder === 'asc') {
        return compareA > compareB ? 1 : -1;
      } else {
        return compareA < compareB ? 1 : -1;
      }
    });

  // Pagination
  const totalPages = Math.ceil(filteredTrips.length / itemsPerPage);
  const paginatedTrips = filteredTrips.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  // Toggle sort order
  const _toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  if (loading) {
    return (
      <DashboardLayout userRole="customer" activeNavId="trips" onNavigate={handleNavigate}>
        <LoadingSpinner message="Loading trips..." className="min-h-screen" />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout userRole="customer" activeNavId="trips" onNavigate={handleNavigate}>
        <ErrorAlert message={error} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole="customer"
      activeNavId="trips"
      onNavigate={handleNavigate}
    >
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Trips</h1>
            <p className="mt-1 text-sm text-gray-500">
              View and manage all your outdoor adventures
            </p>
          </div>
          <Button onClick={() => navigate('/')}>
            Book New Trip
          </Button>
        </div>

        {/* Filters and Search */}
        <Card>
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Trips</option>
                <option value="upcoming">Upcoming</option>
                <option value="past">Past</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label htmlFor="sortBy" className="block text-sm font-medium text-gray-700 mb-1">
                Sort By
              </label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="date">Date</option>
                <option value="price">Price</option>
                <option value="guide">Guide Name</option>
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <label htmlFor="sortOrder" className="block text-sm font-medium text-gray-700 mb-1">
                Order
              </label>
              <select
                id="sortOrder"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Experience, guide..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Trips List */}
        {paginatedTrips.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="No Trips Found"
            description={
              searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Start booking your next outdoor adventure!'
            }
            action={
              <Button onClick={() => navigate('/')}>
                Browse Experiences
              </Button>
            }
          />
        ) : (
          <div className="space-y-4">
            {paginatedTrips.map((trip) => (
              <Card
                key={trip.bookingId}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/customer/trips/${trip.bookingId}`)}
              >
                <div className="flex gap-6">
                  {/* Trip Image */}
                  <div className="flex-shrink-0">
                    {trip.experienceImage ? (
                      <img
                        src={trip.experienceImage}
                        alt={trip.experienceName}
                        className="w-32 h-32 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-32 h-32 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                        <Calendar className="w-12 h-12 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Trip Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {trip.experienceName}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          with {trip.guideName}
                          {trip.guideRating && (
                            <span className="ml-2 inline-flex items-center">
                              <_Star className="w-4 h-4 text-yellow-400 fill-current" />
                              <span className="ml-1">{trip.guideRating.toFixed(1)}</span>
                            </span>
                          )}
                        </p>
                      </div>
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(trip.status)}`}>
                        {trip.status}
                      </span>
                    </div>

                    {/* Trip Info Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(trip.tripDate)}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <_Users className="w-4 h-4" />
                        <span>{trip.guestCount} {trip.guestCount === 1 ? 'guest' : 'guests'}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <_MapPin className="w-4 h-4" />
                        <span className="truncate">{trip.location?.name}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <DollarSign className="w-4 h-4" />
                        <span className="font-semibold">{formatCentsToDollars(trip.totalPaidCents)}</span>
                      </div>
                    </div>

                    {/* Balance Due Alert */}
                    {trip.balanceDueCents > 0 && (
                      <div className="mt-3 flex items-center space-x-2 p-3 bg-orange-50 border border-orange-200 rounded-md">
                        <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0" />
                        <p className="text-sm text-orange-800">
                          <strong>Balance Due:</strong> {formatCentsToDollars(trip.balanceDueCents)}
                        </p>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="mt-4 flex items-center space-x-3">
                      <button
                        onClick={(_e) => {
                          _e.stopPropagation();
                          navigate(`/customer/trips/${trip.bookingId}`);
                        }}
                        className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        View Details →
                      </button>
                      {trip.canReview && (
                        <button
                          onClick={(_e) => {
                            _e.stopPropagation();
                            navigate(`/customer/reviews/new?booking=${trip.bookingId}`);
                          }}
                          className="text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors"
                        >
                          Write Review
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, filteredTrips.length)} of{' '}
              {filteredTrips.length} trips
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
